/**
 * <ImportStatusLine /> — the "✦ {what AI is doing right now}" line
 * inside ImportDashboard.
 *
 * Per BUILD_READINESS_M29.md acceptance criterion 12:
 *   ✦ sparkle in brand teal + status text. Subtle 1.5s pulse on
 *   sparkle while AI is actively working. Stops pulsing on idle
 *   (acceptance criterion 18 — no looping animations on idle UI).
 *
 * `idle = true` is the default for static contexts (past imports,
 * paused sessions, waiting-for-HR-input). `idle = false` keeps
 * the pulse alive.
 *
 * `tone` lets the sparkle shift between brand-teal (default —
 * "AI working"), brand-blue ("AI thinking"), and rose
 * ("AI hit a problem"). Static + accessible — the text is the
 * source of truth, the color is a glance signal.
 */

import { AlertCircle, Pause, Sparkles } from "lucide-react";
import type { ReactNode } from "react";

export interface ImportStatusLineProps {
  /** The status text shown to the right of the sparkle. */
  message: string;
  /** When false, sparkle pulses; when true, the icon is static. */
  idle?: boolean;
  /** Optional tonal hint — defaults to brand teal. */
  tone?: "working" | "thinking" | "waiting" | "error";
  /** Optional right-aligned action (e.g. an Abandon button). */
  trailing?: ReactNode;
}

export function ImportStatusLine({
  message,
  idle = false,
  tone = "working",
  trailing,
}: ImportStatusLineProps) {
  let Icon: typeof Sparkles = Sparkles;
  let iconCls = "text-brand-teal";

  if (tone === "thinking") iconCls = "text-brand-blue";
  if (tone === "waiting") {
    Icon = Pause;
    iconCls = "text-text-tertiary";
  }
  if (tone === "error") {
    Icon = AlertCircle;
    iconCls = "text-rose-600";
  }

  return (
    <div
      className="flex items-center justify-between gap-3"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Icon
          size={14}
          className={`shrink-0 ${iconCls} ${
            !idle && tone === "working" ? "motion-safe:animate-pulse" : ""
          }`}
          aria-hidden="true"
        />
        <span className="truncate text-xs text-text-secondary">
          {message}
        </span>
      </div>
      {trailing && <div className="shrink-0">{trailing}</div>}
    </div>
  );
}
